/* eslint-disable react/display-name */
import React, { Fragment, createRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import BootstrapTable from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row, Spinner } from 'reactstrap';
import PaginationActions from '../../helpers/PaginationActions';
import { tableWaitLoadTime } from '../../helpers/constants';

const paginationActions = new PaginationActions();

const waitForData = tableWaitLoadTime;

const ariaSort = { asc: 'ascending', desc: 'descending' };

/*
What the table library leaves out for keyboard and screen-reader users: sortable headers sort on
Enter and Space and say which way they are sorted; the action column's empty header gets a name.
`sort` is the { field, order } the table was last sorted by, kept by the caller through `onSort`.
*/
export const makeColumnsAccessible = (columns, sort, onSort) =>
  columns.map(column => {
    if (!column.sort) {
      // the header cell anchors the visually hidden text; unanchored, it sat outside the table's
      // scroll box and made the whole page scroll sideways at 320 px (WCAG 1.4.10)
      return column.text
        ? column
        : {
            ...column,
            headerClasses: [column.headerClasses, 'position-relative'].filter(Boolean).join(' '),
            headerFormatter: () => <span className="sr-only">Actions</span>
          };
    }

    return {
      ...column,
      onSort: (field, order) => {
        onSort({ field, order });
        column.onSort && column.onSort(field, order);
      },
      headerAttrs: {
        ...column.headerAttrs,
        'aria-sort': sort.field === column.dataField ? ariaSort[sort.order] : undefined
      },
      headerEvents: {
        ...column.headerEvents,
        onKeyDown: event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
          }
        }
      }
    };
  });

// the library makes every header a tab stop; only the sortable ones do anything, so the others leave the tab order
export const useHeadersWithoutSortOutOfTabOrder = containerRef =>
  useLayoutEffect(() => {
    containerRef.current &&
      containerRef.current
        .querySelectorAll('th:not(.sortable)')
        .forEach(header => header.setAttribute('tabindex', '-1'));
  });

const INBDETable = ({ data, columns }) => {
  const [hideLoader, setHideLoader] = useState(false);
  const [sort, setSort] = useState({});
  let table = createRef();
  const mountedRef = useRef(null);
  const containerRef = useRef(null);
  useHeadersWithoutSortOutOfTabOrder(containerRef);

  const tableLoader = () => {
    return (
      <div className="d-flex min-vh-50 align-items-center justify-content-center">
        {hideLoader ? <p>No results found</p> : <Spinner color="primary" className="avatar-3xl" />}
      </div>
    );
  };

  const options = {
    custom: true,
    sizePerPage: 20,
    totalSize: data.length
  };

  useEffect(() => {
    mountedRef.current = true;
    if (data && data.length) {
      mountedRef.current && setHideLoader(true);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [data]);

  setTimeout(() => {
    mountedRef.current && setHideLoader(true);
  }, waitForData);

  return (
    <PaginationProvider pagination={paginationFactory(options)}>
      {({ paginationProps, paginationTableProps }) => {
        const lastIndex = paginationProps.page * paginationProps.sizePerPage;

        return (
          <Fragment>
            <div className="table-responsive" ref={containerRef}>
              <BootstrapTable
                ref={table}
                bootstrap4
                keyField="id"
                data={data}
                columns={makeColumnsAccessible(columns, sort, setSort)}
                bordered={false}
                classes="table-dashboard table-sm fs--1 border-bottom mb-0"
                rowClasses="btn-reveal-trigger"
                headerClasses="bg-200 text-900"
                noDataIndication={() => tableLoader()}
                {...paginationTableProps}
              />
            </div>
            <Row noGutters className="px-1 py-3">
              <Col xs="auto" className="ml-auto">
                <Button
                  color="falcon-default"
                  size="sm"
                  aria-label="First page"
                  onClick={paginationActions.handleFirstPage(paginationProps)}
                  disabled={paginationProps.page === 1}
                >
                  <FontAwesomeIcon icon="angle-double-left" />
                </Button>
                <Button
                  color="falcon-default"
                  size="sm"
                  aria-label="Previous page"
                  onClick={paginationActions.handlePrevPage(paginationProps)}
                  disabled={paginationProps.page === 1}
                >
                  <FontAwesomeIcon icon="angle-left" />
                </Button>
                {paginationActions
                  .getPaginationArray(paginationProps.totalSize, paginationProps.sizePerPage, paginationProps.page)
                  .map(pageNo => (
                    <Button
                      color={paginationProps.page === pageNo ? 'falcon-primary' : 'falcon-default'}
                      size="sm"
                      className="ml-2"
                      aria-current={paginationProps.page === pageNo ? 'page' : undefined}
                      onClick={() => paginationProps.onPageChange(pageNo)}
                      key={pageNo}
                    >
                      {pageNo}
                    </Button>
                  ))}
                <Button
                  color="falcon-default"
                  size="sm"
                  className="ml-2"
                  aria-label="Next page"
                  onClick={paginationActions.handleNextPage(paginationProps)}
                  disabled={lastIndex >= paginationProps.totalSize}
                >
                  <FontAwesomeIcon icon="angle-right" />
                </Button>
                <Button
                  color="falcon-default"
                  size="sm"
                  className="ml-2"
                  aria-label="Last page"
                  onClick={paginationActions.handleLastPage(
                    paginationProps,
                    paginationProps.totalSize,
                    paginationProps.sizePerPage
                  )}
                  disabled={lastIndex >= paginationProps.totalSize}
                >
                  <FontAwesomeIcon icon="angle-double-right" />
                </Button>
              </Col>
            </Row>
          </Fragment>
        );
      }}
    </PaginationProvider>
  );
};

INBDETable.propTypes = {
  value: PropTypes.any,
  data: PropTypes.array,
  columns: PropTypes.array
};
INBDETable.defaultProps = { value: `INBDETable`, data: [], columns: [] };

export default INBDETable;
